import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useMatches } from 'react-router';
import { hasProperty } from '@gonfalon/collections/hasProperty';

/**
 * Given all of the nested routes that match the current path,
 * return the component with the given name that is exported
 * as part of the "handle" object from the deepest matching route module.
 *
 * @param props.name - The name of the component to get.
 * @returns An object containing the Component and route data, or undefined if the component is not found.
 */
export function useRouteHandleComponent(props: { name: string }) {
  const matches = useMatches();

  return useMemo(() => {
    const reversedMatches = [...matches].reverse();

    for (const match of reversedMatches) {
      // check if the match has a "handle" object property that contains a function with the given name
      if (hasProperty(match.handle, props.name) && typeof match.handle[props.name] === 'function') {
        // if it does, we assume that the function is a React component
        return {
          Component: match.handle[props.name] as (props: { [name: string]: unknown }) => ReactNode,
          data: match.loaderData,
        };
      }
    }

    return undefined;
  }, [matches, props.name]);
}
